import * as React from "react";
import { clsx } from "clsx";

interface ResizeState {
  pointerId: number;
  startX: number;
  startWidth: number;
}

interface Props {
  columnId: string;
  label: string;
  width: number;
  onResize: (columnId: string, width: number) => void;
}

/**
 * Pointer-driven column resize grip. Captures the pointer, reports the live width to
 * `onResize`, and stops propagation so the surrounding header never sees a click,
 * sort, or drag-and-drop activation.
 */
export const ColumnResizeHandle: React.FC<Props> = ({ columnId, label, width, onResize }) => {
  const [isResizing, setIsResizing] = React.useState(false);
  const resizeStateRef = React.useRef<ResizeState | null>(null);
  const restoreBodyStyleRef = React.useRef<(() => void) | null>(null);

  const stopResize = React.useCallback((target?: HTMLButtonElement | null) => {
    const state = resizeStateRef.current;
    if (!state) return;

    resizeStateRef.current = null;
    setIsResizing(false);
    restoreBodyStyleRef.current?.();
    restoreBodyStyleRef.current = null;

    if (target?.hasPointerCapture(state.pointerId)) {
      target.releasePointerCapture(state.pointerId);
    }
  }, []);

  React.useEffect(() => () => stopResize(), [stopResize]);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
    };
    setIsResizing(true);

    if (typeof document !== "undefined") {
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      restoreBodyStyleRef.current = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
      };
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = resizeStateRef.current;
    if (!state) return;

    event.preventDefault();
    event.stopPropagation();
    onResize(columnId, state.startWidth + event.clientX - state.startX);
  };

  return (
    <button
      type="button"
      aria-label={`Resize column ${label}`}
      title="Drag to resize column"
      className={clsx(
        "absolute right-0 top-0 z-[60] h-full w-2 cursor-col-resize touch-none outline-none",
        "after:absolute after:right-0 after:top-2 after:h-[calc(100%-1rem)] after:w-px after:bg-transparent after:transition-colors",
        "hover:after:bg-coral focus-visible:after:bg-coral",
        isResizing && "after:bg-coral",
      )}
      onClick={event => event.stopPropagation()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={event => {
        event.preventDefault();
        event.stopPropagation();
        stopResize(event.currentTarget);
      }}
      onPointerCancel={event => stopResize(event.currentTarget)}
      onLostPointerCapture={() => stopResize()}
    />
  );
};
